<script lang="ts" setup>

import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { Options } from './options'
import { IModalFormOptions } from '../ModalForm'
import ModalForm from '../ModalForm/ModalForm.vue'

import { deleteBundle, IDeleteBody } from '@/api/delete'
import { changePublicBundle, IPublicBody } from '@/api/public'
import { unDelete } from '@/api/unDelete'
import { useResponsive } from '@/composables/useResponsive'
import { showError, showSuccess } from '@/notifications'
import { useConfigStore } from '@/stores/configStore'
import { IViewConfig } from '@/types/serverField.type'

interface IAccess {
  Add: boolean
  Delete: boolean
  Edit: boolean
  Export: boolean
  Print: boolean
  Read: boolean
  Undelete: boolean
}

// TODO_VUE: везде где используем данный компонент - нужно передавать Access,
// далее эту константу можно будет убрать
const DEFAULT_ACCESS: IAccess = {
  Add: true,
  Delete: true,
  Edit: true,
  Export: true,
  Print: true,
  Read: true,
  Undelete: true
}

const { access, options = new Options({}), uuid, title, isDeleted, readonly, showPublicBtn, showUnPublicBtn, link, externalLink, printPath, downloadPath, reloadData, recordEditForm: recordEditFormProp } = defineProps<{
  access?: IAccess | undefined
  options?: any
  recordEditForm?: IModalFormOptions | IViewConfig['RecordEditForm']
  uuid: string
  title: string
  isDeleted: boolean
  readonly?: boolean
  showPublicBtn?: boolean
  showUnPublicBtn?: boolean
  link?: string
  externalLink?: string
  printPath?: string
  downloadPath?: string
  reloadData:() => void
}>()

const preparedAccess = access || DEFAULT_ACCESS

if (access === undefined) {
  console.warn('Access is undefined')
}

// Достанем из конфига возможные параметры для редактирования записи в модальном окне
const configStore = useConfigStore(options.viewName)
const recordEditForm = computed<IModalFormOptions>(() => {
  const form = (recordEditFormProp || configStore.config?.RecordEditForm) as Partial<IModalFormOptions>

  return {
    ...form,
    FormUuid: form?.FormUuid ?? ''
  }
})

const recordEditModalRef = ref(false)
const showRecordEditForm = () => {
  recordEditModalRef.value = true
}

const deleteDialog = ref(false)
const unDeleteDialog = ref(false)
const unPublicDialog = ref(false)
const publicDialog = ref(false)

const routeExists = (route) => {
  const router = useRouter()
  return router.hasRoute(route)
}

const deleteData: IDeleteBody = {
  __type: options.config.deleteType,
  __uuid: uuid
}

if (options.config.adapter) {
  deleteData.__adapter = options.config.adapter
}

const deleteAction = async () => {
  const res = await deleteBundle(deleteData)
  if (res.done) {
    reloadData()
    deleteDialog.value = false
    const msg = title ? `Запись с названием "${title}" успешно удалена.` : 'Запись удалена.'
    showSuccess(msg)
  } else {
    showError(res.msg)
  }
}

const unDeleteAction = async () => {
  const res = await unDelete(deleteData)
  if (res.done) {
    reloadData()
    unDeleteDialog.value = false
    const msg = title ? `Запись с названием "${title}" успешно восстановлена.` : 'Запись восстановлена.'
    showSuccess(msg)
  } else {
    showError(res.msg)
  }
}

// Снятие с публикации: закрываем диалог, показываем уведомление и перезагружаем данные
const unPublicAction = async () => {
  const changePublicData: IPublicBody = {
    __type: options.config.deleteType,
    __id: uuid,
    __action: "edit",
    __adapter: options?.config?.adapter,
    __fields: [
      {
        Name: 'Published',
        Value: false
      }
    ]
  }
  try {
    await changePublicBundle(changePublicData)
    unPublicDialog.value = false
    showSuccess('Запись снята с публикации.')
    reloadData()
  } catch {
    // ошибки показывает api-функция (showError)
  }
}

// Публикация: закрываем диалог, показываем уведомление и перезагружаем данные
const publicAction = async () => {
  const changePublicData: IPublicBody = {
    __type: options.config.deleteType,
    __id: uuid,
    __action: "edit",
    __adapter: options?.config?.adapter,
    __fields: [
      {
        Name: 'Published',
        Value: true
      }
    ]
  }
  try {
    await changePublicBundle(changePublicData)
    publicDialog.value = false
    showSuccess('Запись опубликована.')
    reloadData()
  } catch {
    // ошибки показывает api-функция (showError)
  }
}

const { isMobile } = useResponsive()

</script>

<template>
  <div
    v-if="!isMobile"
    class="mw-100 text-no-wrap"
  >
    <VIcon
      v-if="showUnPublicBtn"
      :title="options?.unPublicButtonTitle"
      color="primary"
      icon="mdi-eye-off-outline"
      size="large"
      data-testid="entity-unpublic"
      @click="() => unPublicDialog = !unPublicDialog"
    />
    <VIcon
      v-if="showPublicBtn"
      :title="options?.publicButtonTitle"
      color="primary"
      icon="mdi-eye-outline"
      size="large"
      data-testid="entity-public"
      @click="() => publicDialog = !publicDialog"
    />
    <RouterLink
      v-if="preparedAccess.Read && (link || routeExists(options.entityRouteName)) && !recordEditForm?.AdapterUuid"
      :to="link || { name: options.entityRouteName, params: { uuid }, query: { tab: 'preview' } }"
      data-testid="entity-preview-link"
    >
      <VIcon
        title="Просмотр"
        color="primary"
        icon="mdi-card-search-outline"
        size="large"
      />
    </RouterLink>
    <template v-if="!readonly">
      <RouterLink
        v-if="preparedAccess.Export && downloadPath"
        :to="`/${downloadPath}`"
        :download="title || 'download'"
        target="_blank"
        data-testid="entity-download-link"
      >
        <VIcon
          title="Скачать"
          color="primary"
          icon="mdi-download"
          size="large"
        />
      </RouterLink>
      <RouterLink
        v-if="preparedAccess.Print && printPath"
        :to="`/${printPath}`"
        target="_blank"
      >
        <VIcon
          title="Печать"
          color="primary"
          icon="mdi-printer-outline"
          size="large"
        />
      </RouterLink>
      <template v-if="!isDeleted && preparedAccess.Edit">
        <VIcon
          v-if="recordEditForm?.AdapterUuid"
          title="Редактировать"
          color="primary"
          icon="mdi-pencil"
          size="large"
          data-testid="entity-edit-form"
          @click="showRecordEditForm()"
        />

        <RouterLink
          v-else-if="(link || routeExists(options.entityRouteName))"
          :to="link ? (link + '?tab=edit') : {name: options.entityRouteName, params: { uuid }, query: { from: 'list', tab: 'edit' } }"
          data-testid="entity-edit-link"
        >
          <VIcon
            title="Редактировать"
            color="primary"
            icon="mdi-pencil"
            size="large"
          />
        </RouterLink>
      </template>
      <VIcon
        v-if="isDeleted && preparedAccess.Undelete"
        title="Восстановить"
        color="primary"
        icon="mdi-undo-variant"
        size="large"
        data-testid="entity-undelete"
        @click="() => unDeleteDialog = !unDeleteDialog"
      />
      <VIcon
        v-else-if="!isDeleted && preparedAccess.Delete"
        title="Удалить"
        color="primary"
        icon="mdi-trash-can-outline"
        size="large"
        data-testid="entity-delete"
        @click="() => deleteDialog = !deleteDialog"
      />
    </template>
    <RouterLink
      v-if="preparedAccess.Read && externalLink"
      :to="externalLink"
      rel="noopener noreferrer"
      data-testid="entity-external-link"
    >
      <VIcon
        title="Открыть материал"
        color="primary"
        icon="mdi-link"
        size="large"
      />
    </RouterLink>
  </div>

  <div v-else>
    <VMenu
      activator="parent"
      offset-y
    >
      <template #activator="{ props }">
        <VIcon
          title="Меню действий"
          icon="mdi-dots-horizontal"
          size="large"
          data-testid="entity-actions-menu"
          v-bind="props"
        />
      </template>
      <VList>
        <VListItem
          v-if="showUnPublicBtn"
          data-testid="entity-unpublic"
          @click="unPublicDialog = true"
        >
          <div class="d-flex align-center">
            <VListItemIcon class="mr-2">
              <VIcon icon="mdi-eye-off-outline" />
            </VListItemIcon>
            <VListItemTitle>{{ options?.unPublicButtonTitle }}</VListItemTitle>
          </div>
        </VListItem>
        <VListItem
          v-if="showPublicBtn"
          data-testid="entity-public"
          @click="publicDialog = true"
        >
          <div class="d-flex align-center">
            <VListItemIcon class="mr-2">
              <VIcon icon="mdi-eye-outline" />
            </VListItemIcon>
            <VListItemTitle>{{ options?.publicButtonTitle }}</VListItemTitle>
          </div>
        </VListItem>
        <VListItem
          v-if="preparedAccess.Read && (link || routeExists(options.entityRouteName)) && !recordEditForm?.AdapterUuid"
          :to="link || { name: options.entityRouteName, params: { uuid }, query: { tab: 'preview' } }"
          data-testid="entity-preview-link"
        >
          <div class="d-flex align-center">
            <VListItemIcon class="mr-2">
              <VIcon icon="mdi-card-search-outline" />
            </VListItemIcon>
            <VListItemTitle>Просмотр</VListItemTitle>
          </div>
        </VListItem>
        <VListItem
          v-if="preparedAccess.Export && downloadPath"
          :href="downloadPath"
          :download="title || 'download'"
          target="_blank"
          data-testid="entity-download-link"
        >
          <div class="d-flex align-center">
            <VListItemIcon class="mr-2">
              <VIcon icon="mdi-download" />
            </VListItemIcon>
            <VListItemTitle>Скачать</VListItemTitle>
          </div>
        </VListItem>
        <VListItem
          v-if="preparedAccess.Print && printPath"
          :to="`/${printPath}`"
          target="_blank"
        >
          <div class="d-flex align-center">
            <VListItemIcon class="mr-2">
              <VIcon icon="mdi-printer-outline" />
            </VListItemIcon>
            <VListItemTitle>Печать</VListItemTitle>
          </div>
        </VListItem>
        <template v-if="!isDeleted && preparedAccess.Edit">
          <VListItem
            v-if="recordEditForm?.AdapterUuid"
            data-testid="entity-edit-form"
            @click="showRecordEditForm()"
          >
            <div class="d-flex align-center">
              <VListItemIcon class="mr-2">
                <VIcon icon="mdi-pencil" />
              </VListItemIcon>
              <VListItemTitle>Редактировать</VListItemTitle>
            </div>
          </VListItem>
          <VListItem
            v-else-if="(link || routeExists(options.entityRouteName))"
            data-testid="entity-edit-link"
            :to="link ? (link + '?tab=edit') : {name: options.entityRouteName, params: { uuid }, query: { from: 'list', tab: 'edit' } }"
          >
            <div class="d-flex align-center">
              <VListItemIcon class="mr-2">
                <VIcon icon="mdi-pencil" />
              </VListItemIcon>
              <VListItemTitle>Редактировать</VListItemTitle>
            </div>
          </VListItem>
        </template>
        <VListItem
          v-if="isDeleted && preparedAccess.Undelete"
          class="v-list-item--inline"
          data-testid="entity-undelete"
          @click="unDeleteDialog = true"
        >
          <div class="d-flex align-center">
            <VListItemIcon class="mr-2">
              <VIcon icon="mdi-undo-variant" />
            </VListItemIcon>
            <VListItemTitle>Восстановить</VListItemTitle>
          </div>
        </VListItem>
        <VListItem
          v-else-if="!isDeleted && preparedAccess.Delete"
          data-testid="entity-delete"
          class="v-list-item--inline"
          @click="deleteDialog = true"
        >
          <div class="d-flex align-center">
            <VListItemIcon class="mr-2">
              <VIcon icon="mdi-trash-can-outline" />
            </VListItemIcon>
            <VListItemTitle>Удалить</VListItemTitle>
          </div>
        </VListItem>
        <VListItem
          v-if="preparedAccess.Read && externalLink"
          :to="externalLink"
          rel="noopener noreferrer"
          data-testid="entity-external-link"
        >
          <div class="d-flex align-center">
            <VListItemIcon class="mr-2">
              <VIcon icon="mdi-link" />
            </VListItemIcon>
            <VListItemTitle>Открыть материал</VListItemTitle>
          </div>
        </VListItem>
      </VList>
    </VMenu>
  </div>

  <VDialog
    v-if="preparedAccess.Undelete"
    v-model="unDeleteDialog"
    data-testid="entity-undelete-dialog"
  >
    <VCard
      class="mx-auto"
      max-width="550"
      activator="parent"
    >
      <VDivider />
      <VContainer>
        <VCardText class="text-h6">
          {{ options.unDeleteFormTitle }}
        </VCardText>
        <VCardActions>
          <VBtn
            variant="elevated"
            color="success"
            @click="unDeleteAction"
          >
            Восстановить
          </VBtn>
          <VBtn @click="unDeleteDialog = false">
            Отмена
          </VBtn>
        </VCardActions>
      </VContainer>
    </VCard>
  </VDialog>
  <VDialog
    v-if="preparedAccess.Delete"
    v-model="deleteDialog"
    data-testid="entity-delete-dialog"
  >
    <VCard
      class="mx-auto"
      max-width="550"
      activator="parent"
    >
      <VDivider />
      <VContainer>
        <VCardText class="text-h6">
          {{ options.deleteFormTitle }}
        </VCardText>
        <VCardActions>
          <VBtn
            variant="elevated"
            color="error"
            @click="deleteAction"
          >
            Удалить
          </VBtn>
          <VBtn @click="deleteDialog = false">
            Отмена
          </VBtn>
        </VCardActions>
      </VContainer>
    </VCard>
  </VDialog>

  <VDialog
    v-if="showUnPublicBtn"
    v-model="unPublicDialog"
    data-testid="entity-unpublic-dialog"
  >
    <VCard
      class="mx-auto"
      max-width="550"
      activator="parent"
    >
      <VDivider />
      <VContainer>
        <VCardText class="text-h6">
          {{ options.unPublicFormTitle }}
        </VCardText>
        <VCardActions>
          <VBtn
            variant="elevated"
            color="error"
            @click="unPublicAction"
          >
            {{ options.unPublicButtonTitle }}
          </VBtn>
          <VBtn @click="unPublicDialog = false">
            Отмена
          </VBtn>
        </VCardActions>
      </VContainer>
    </VCard>
  </VDialog>
  <VDialog
    v-if="showPublicBtn"
    v-model="publicDialog"
    data-testid="entity-public-dialog"
  >
    <VCard
      class="mx-auto"
      max-width="550"
      activator="parent"
    >
      <VDivider />
      <VContainer>
        <VCardText class="text-h6">
          {{ options.publicFormTitle }}
        </VCardText>
        <VCardActions>
          <VBtn
            variant="elevated"
            color="error"
            @click="publicAction"
          >
            {{ options.publicButtonTitle }}
          </VBtn>
          <VBtn @click="publicDialog = false">
            Отмена
          </VBtn>
        </VCardActions>
      </VContainer>
    </VCard>
  </VDialog>

  <ModalForm
    v-if="recordEditModalRef"
    v-model="recordEditModalRef"
    data-testid="entity-edit-modal"
    :options="recordEditForm"
    :params="{ Uuid: uuid }"
    :record-uuid="uuid"
    @submit="reloadData"
  />
</template>
